import { ReactNode, useState } from "react";
import { Link } from "wouter";
import type { Store } from "@workspace/api-client-react";
import { ArrowLeft, User } from "lucide-react";
import { useStoreColors } from "@/hooks/use-store-colors";
import { useCustomerAuth } from "@/hooks/use-customer-auth";
import { CustomerLoginModal } from "@/components/CustomerLoginModal";
import { BrandTile } from "@/components/BrandTile";

function formatWhatsapp(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("55")) {
    const ddd = digits.slice(2, 4);
    const part1 = digits.slice(4, 9);
    const part2 = digits.slice(9, 13);
    return `(${ddd}) ${part1}-${part2}`;
  }
  if (digits.length === 11) {
    const ddd = digits.slice(0, 2);
    const part1 = digits.slice(2, 7);
    const part2 = digits.slice(7, 11);
    return `(${ddd}) ${part1}-${part2}`;
  }
  if (digits.length === 10) {
    const ddd = digits.slice(0, 2);
    const part1 = digits.slice(2, 6);
    const part2 = digits.slice(6, 10);
    return `(${ddd}) ${part1}-${part2}`;
  }
  return raw;
}

interface CatalogLayoutProps {
  children: ReactNode;
  store: Store;
  cartItemCount: number;
  onOpenCart: () => void;
  showBack?: string;
  isOpen: boolean;
}

export function CatalogLayout({ children, store, cartItemCount, onOpenCart, showBack, isOpen }: CatalogLayoutProps) {
  useStoreColors(store);
  const { customer } = useCustomerAuth();
  const [loginOpen, setLoginOpen] = useState(false);

  return (
    // pb-24 = o espaço do "Ver Carrinho" flutuante; só com item no carrinho. Sem ele, o fim da lista encosta no pé da
    // tela — é lá que a propaganda do Venda no Zap das lojas grátis fica quando há pouco produto (Storefront).
    <div className={`min-h-[100svh] bg-gray-100 flex flex-col ${cartItemCount > 0 ? "pb-24" : ""}`}>
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-card shadow-sm border-b border-border/50">
        <div className="px-4 py-3 max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showBack && (
              <Link href={showBack} className="p-2 -ml-2 rounded-full hover:bg-muted text-muted-foreground transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            )}
            
            <div className="flex items-center gap-3">
              {store.logoUrl ? (
                <img src={store.logoUrl} alt={store.name} className="w-10 h-10 rounded-full object-cover border border-border/50 shadow-sm" />
              ) : (
                // Fallback institucional do Venda no Zap quando a loja ainda
                // não subiu logo própria. Antes era um círculo com a letra
                // inicial — sem marca, sem identidade. BrandTile mantém
                // contexto de "esta loja roda no Venda no Zap".
                <BrandTile size="sm" className="w-10 h-10 rounded-full" />
              )}
              <div>
                <h1 className="font-bold text-foreground leading-tight">{store.name}</h1>
                <p className="text-xs text-muted-foreground pl-[1px]">Loja Digital</p>
              </div>
            </div>
          </div>
          {customer ? (
            <Link
              href={`/conta?store=${store.slug}`}
              aria-label="Minha conta"
              className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-full hover:bg-muted/60 transition-colors"
            >
              <div className="flex flex-col leading-tight text-right min-w-0 max-w-[9rem]">
                <span className="text-sm font-semibold text-foreground truncate">
                  {customer.firstName}
                </span>
                <span className="text-[11px] text-muted-foreground truncate">
                  {customer.whatsappFormattedNumber || formatWhatsapp(customer.whatsappNumber)}
                </span>
              </div>
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center overflow-hidden border border-border/50 shadow-sm">
                {customer.avatarBase64 ? (
                  <img
                    src={customer.avatarBase64.startsWith("data:") ? customer.avatarBase64 : `data:image/jpeg;base64,${customer.avatarBase64}`}
                    alt={customer.firstName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-5 h-5" />
                )}
              </div>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setLoginOpen(true)}
              aria-label="Entrar com WhatsApp"
              className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center hover:bg-muted/80 transition-colors"
            >
              <User className="w-5 h-5" />
            </button>
          )}
        </div>
      </header>
      <CustomerLoginModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        dismissible
        askBirthDate={(store as any)?.askBirthDate}
        birthDateRequired={(store as any)?.birthDateRequired}
        storeSlug={store.slug}
        loyaltyEnabled={(store as any)?.loyaltyEnabled === true}
      />
      {/* Main Content */}
      {/* Sem `bg-card` aqui: no celular este main ocupa a largura toda e a folha
          branca cobria o cinza da página — os cards brancos ficavam sobre branco.
          O fundo é o do wrapper; no desktop a borda e a sombra continuam
          desenhando a folha. */}
      <main className="w-full max-w-3xl mx-auto flex-1 flex flex-col shadow-sm sm:rounded-b-2xl sm:border-x sm:border-b sm:border-border/50 sm:mb-8">
        {children}
      </main>
    </div>
  );
}
